(() => {
  if (!customElements.get('tone-finder')) {
    class ToneFinder extends HTMLElement {
      connectedCallback() {
        if (this.controller) return;
        this.steps = [...this.querySelectorAll('[data-finder-step]')];
        this.results = [...this.querySelectorAll('[data-finder-result]')];
        this.resultPanel = this.querySelector('[data-finder-results]');
        this.navigation = this.querySelector('[data-finder-navigation]');
        this.previous = this.querySelector('[data-finder-prev]');
        this.next = this.querySelector('[data-finder-next]');
        this.actions = this.querySelector('[data-finder-result-actions]');
        this.allButton = this.querySelector('[data-finder-all]');
        this.resultHeading = this.querySelector('[data-finder-result-heading]');
        this.status = this.querySelector('[data-finder-status]');
        if (this.steps.length !== 2 || !this.results.length || !this.navigation) return;
        if (this.steps.some(step => !step.querySelector('[data-finder-answer]'))) return;
        this.controller = new AbortController();
        const {signal} = this.controller;
        this.candidates = this.results.map(element => ({
          element,
          score: Math.max(1, Math.min(10, Number(element.dataset.score) || 1)),
          pairs: new Set((element.dataset.matchTags || '').split(/[,\n]+/).map(pair => pair.split('|').map(key => key.trim().toLowerCase()).join('|')).filter(pair => pair.split('|').length === 2)),
        }));
        this.currentStep = 0;
        this.previous.addEventListener('click', () => this.showStep(Math.max(0, this.currentStep - 1), true), {signal});
        this.next.addEventListener('click', () => {
          if (this.answers(this.currentStep).length) this.showStep(this.currentStep + 1, true);
        }, {signal});
        this.querySelector('[data-finder-reset]').addEventListener('click', () => {
          this.querySelectorAll('[data-finder-answer]').forEach(input => { input.checked = false; });
          this.showStep(0, true);
        }, {signal});
        this.allButton.addEventListener('click', () => this.showResults(null, true), {signal});
        this.addEventListener('change', event => {
          if (!event.target.matches('[data-finder-answer]')) return;
          this.next.disabled = !this.answers(this.currentStep).length;
          this.status.textContent = this.dataset.selectedMessage.replace('[count]', String(this.answers(this.currentStep).length));
        }, {signal});
        this.addEventListener('shopify:block:select', event => {
          const result = event.target.closest('[data-finder-result]');
          const question = event.target.closest('[data-finder-step]');
          if (result && this.contains(result)) {
            this.currentStep = 2;
            this.steps.forEach(step => { step.hidden = true; });
            this.previous.disabled = false;
            this.next.hidden = true;
            this.actions.hidden = false;
            this.showResults(result, false);
          } else if (question && this.contains(question)) {
            this.showStep(Number(question.dataset.finderStep), false);
          }
        }, {signal});
        this.navigation.hidden = false;
        this.showStep(0, false);
      }

      answers(step) {
        return [...new Set([...this.querySelectorAll(`[data-finder-answer="${step}"]:checked`)].map(input => input.value.trim().toLowerCase()))];
      }

      choose() {
        const scores = new Map();
        for (const drink of this.answers(0)) {
          for (const taste of this.answers(1)) {
            for (const candidate of this.candidates) {
              if (candidate.pairs.has(`${drink}|${taste}`)) {
                scores.set(candidate.element, (scores.get(candidate.element) || 0) + candidate.score);
              }
            }
          }
        }
        let winner = null;
        let maximum = 0;
        for (const [element, score] of scores) {
          if (score > maximum) {
            winner = element;
            maximum = score;
          }
        }
        return winner;
      }

      showStep(step, focus) {
        this.currentStep = Math.min(2, Math.max(0, step));
        this.steps.forEach((panel, index) => { panel.hidden = index !== this.currentStep; });
        this.resultPanel.hidden = this.currentStep !== 2;
        this.previous.disabled = this.currentStep === 0;
        this.next.hidden = this.currentStep === 2;
        this.next.disabled = this.currentStep === 2 || !this.answers(this.currentStep).length;
        this.next.setAttribute('aria-label', this.currentStep === 1 ? this.dataset.resultLabel : this.dataset.nextLabel);
        this.actions.hidden = this.currentStep !== 2;
        this.status.textContent = '';
        if (this.currentStep === 2) {
          this.showResults(this.choose(), focus);
        } else if (focus) {
          this.steps[this.currentStep].querySelector('legend').focus();
        }
      }

      showResults(winner, focus) {
        this.resultPanel.hidden = false;
        this.resultPanel.toggleAttribute('data-single-result', Boolean(winner));
        this.results.forEach(result => { result.hidden = Boolean(winner) && result !== winner; });
        this.resultHeading.textContent = winner ? this.resultHeading.dataset.matchLabel : this.resultHeading.dataset.allLabel;
        this.allButton.hidden = !winner;
        this.status.textContent = winner ? this.dataset.resultMessage.replace('[product]', winner.dataset.productTitle) : this.resultHeading.dataset.allLabel;
        if (focus) this.resultHeading.focus();
      }

      disconnectedCallback() {
        this.controller?.abort();
        this.controller = null;
      }
    }
    customElements.define('tone-finder', ToneFinder);
  }

  if (!customElements.get('tone-repeat')) {
    class ToneRepeat extends HTMLElement {
      connectedCallback() {
        if (this.controller || !this.hasAttribute('data-animate')) return;
        this.controller = new AbortController();
        const {signal} = this.controller;
        this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
        this.fine = matchMedia('(hover: hover) and (pointer: fine)');
        this.visible = false;
        this.hovered = false;
        const update = () => this.toggleAttribute('data-running', this.visible && !document.hidden && !this.reduced.matches && document.body.dataset.toneMotion !== 'false' && (this.hovered && this.fine.matches || Boolean(this.querySelector(':focus-visible'))));
        this.observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; update(); });
        this.observer.observe(this);
        this.addEventListener('pointerenter', e => { this.hovered = e.pointerType !== 'touch'; update(); }, {signal});
        this.addEventListener('pointerleave', () => { this.hovered = false; update(); }, {signal});
        this.addEventListener('focusin', update, {signal});
        this.addEventListener('focusout', () => queueMicrotask(update), {signal});
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.hovered = false; update(); }, {signal});
        this.reduced.addEventListener('change', update, {signal});
        this.fine.addEventListener('change', () => { this.hovered = false; update(); }, {signal});
        this.settingsObserver = new MutationObserver(update);
        this.settingsObserver.observe(document.body, {attributes: true, attributeFilter: ['data-tone-motion']});
      }
      disconnectedCallback() {
        this.controller?.abort();
        this.observer?.disconnect();
        this.settingsObserver?.disconnect();
        this.controller = null;
        this.removeAttribute('data-running');
      }
    }
    customElements.define('tone-repeat', ToneRepeat);
  }
})();
